import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import SectionEnvNav from '../components/SectionEnvNav';
import RichDocumentRenderer from '../components/RichDocumentRenderer';
import type { Env, PlatformContentManifest, RichDocument, Section, SectionMeta } from '../types';

const MANIFEST_URL = 'platform-content/index.json';

interface SectionPageProps {
  section: Section;
  title: string;
  blurb: string;
}

function isValidEnv(s: string | undefined): s is Env {
  return s === 'test' || s === 'dev' || s === 'uat' || s === 'prod';
}

export default function SectionPage({ section, title, blurb }: SectionPageProps) {
  const { env: envParam } = useParams();
  const env: Env = isValidEnv(envParam) ? envParam : 'test';

  const [manifest, setManifest] = useState<PlatformContentManifest | null>(null);
  const [manifestState, setManifestState] = useState<'loading' | 'loaded' | 'missing'>('loading');
  const [doc, setDoc] = useState<RichDocument | null>(null);
  const [docError, setDocError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setManifestState('loading');
    fetch(MANIFEST_URL, { cache: 'no-cache' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as PlatformContentManifest;
        if (!cancelled) {
          setManifest(json);
          setManifestState('loaded');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setManifest(null);
          setManifestState('missing');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const meta: SectionMeta | undefined = manifest?.envs?.[env]?.[section];
  // Only paths that look like JSON count as published in the native-rendering era.
  // Legacy .html paths from the previous iframe approach are treated as not yet
  // republished, so the placeholder shows instead of a parse error.
  const isPublished = !!meta?.contentPath && meta.contentPath.endsWith('.json');
  const isLegacyPath = !!meta?.contentPath && !meta.contentPath.endsWith('.json');

  useEffect(() => {
    if (!isPublished || !meta?.contentPath) {
      setDoc(null);
      setDocError(null);
      return;
    }
    let cancelled = false;
    setDoc(null);
    setDocError(null);
    fetch(meta.contentPath, { cache: 'no-cache' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as RichDocument;
        if (!cancelled) setDoc(json);
      })
      .catch((err) => {
        if (!cancelled) setDocError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [isPublished, meta?.contentPath]);

  return (
    <div className="page section-page">
      <div className="section-heading">
        <h2>{title}</h2>
        {isPublished && meta?.publishedAt && (
          <span className="section-updated" title={meta.publishedAt}>
            Last updated{' '}
            <strong>
              {new Date(meta.publishedAt).toLocaleString(undefined, {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </strong>{' '}
            for <strong>{env.toUpperCase()}</strong>
          </span>
        )}
      </div>
      <p>{blurb}</p>
      <SectionEnvNav section={section} />

      {manifestState === 'loading' && <p className="tile-loading">Loading…</p>}

      {manifestState !== 'loading' && !isPublished && (
        <div className="section-placeholder">
          {isLegacyPath ? (
            <>
              <p>
                <strong>{title}</strong> for the <strong>{env.toUpperCase()}</strong>{' '}
                environment is being migrated from the legacy HTML format to the new
                native-rendered JSON format.
              </p>
              <p className="note">
                Once that estate's deploy pipeline runs with the new publish step (Phase
                C-2b PR-2), it'll emit a structured <code>infrastructure.json</code> here
                and the page will render natively in CIPD branding.
              </p>
            </>
          ) : (
            <>
              <p>
                <strong>{title}</strong> content for the <strong>{env.toUpperCase()}</strong>{' '}
                environment has not been published yet.
              </p>
              <p className="note">
                Each estate's deploy pipeline emits a per-env document into{' '}
                <code>stsynsiteuks01/platform-content/&lt;platform&gt;/{env}/</code>. Until
                that estate has run a deploy with the publish step enabled, this page will
                stay empty. TEST is the first estate scheduled to publish; DEV / UAT / PROD
                will follow as those estates come online.
              </p>
            </>
          )}
        </div>
      )}

      {isPublished && docError && (
        <p className="tile-error">Could not load content: {docError}</p>
      )}

      {isPublished && !docError && !doc && <p className="tile-loading">Loading content…</p>}

      {isPublished && doc && <RichDocumentRenderer doc={doc} />}
    </div>
  );
}
